import type React from 'react';
import { useCallback } from 'react';
import apiService from '@/services/apiService';
import { convertAbsenceValuesToRatesByYear } from '@/services/utils';
import type {
	AbsenceValues,
	AppData,
	CoverageValues,
	Employee,
	PendingPremiumVars,
	PremiumVarsPayload
} from '@/types/app';
import { DEFAULT_EMPLOYEE } from '@/types/app';

export function usePremiumVarManagement(
	setAppData: React.Dispatch<React.SetStateAction<AppData | null>>,
	setPendingPremiumVars?: React.Dispatch<React.SetStateAction<PendingPremiumVars>>
) {
	const addEmployee = useCallback(() => {
		const newEmployee = {
			...DEFAULT_EMPLOYEE,
			id: `employee-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
		};
		setAppData((prevData) => ({
			...prevData,
			premium_variables: {
				...prevData?.premium_variables,
				employees: {
					...prevData?.premium_variables?.employees,
					employee_list: [
						...(prevData?.premium_variables?.employees?.employee_list || []),
						newEmployee
					]
				}
			}
		}));
		return newEmployee;
	}, [setAppData]);

	const updateEmployee = useCallback(
		(idx: number, field: string, value: unknown) => {
			setAppData((prevData) => ({
				...prevData,
				premium_variables: {
					...prevData?.premium_variables,
					employees: {
						...prevData?.premium_variables?.employees,
						employee_list:
							prevData?.premium_variables?.employees?.employee_list?.map((emp, i) =>
								i === idx ? { ...emp, [field]: value } : emp
							) || []
					}
				}
			}));
		},
		[setAppData]
	);

	const bulkUpdateEmployees = useCallback(
		(field: string, value: unknown, condition: (emp: Employee) => boolean) => {
			setAppData((prevData) => ({
				...prevData,
				premium_variables: {
					...prevData?.premium_variables,
					employees: {
						...prevData?.premium_variables?.employees,
						employee_list:
							prevData?.premium_variables?.employees?.employee_list?.map((emp) =>
								condition(emp) ? { ...emp, [field]: value } : emp
							) || []
					}
				}
			}));
		},
		[setAppData]
	);

	const removeEmployee = useCallback(
		(idx: number) => {
			setAppData((prevData) => ({
				...prevData,
				premium_variables: {
					...prevData?.premium_variables,
					employees: {
						...prevData?.premium_variables?.employees,
						employee_list:
							prevData?.premium_variables?.employees?.employee_list?.filter((_, i) => i !== idx) ||
							[]
					}
				}
			}));
		},
		[setAppData]
	);

	const saveEmployees = useCallback(async (id: string, employees: Employee[]) => {
		await apiService.premiumVars.update(id, {
			employees: { source: 'client', employee_list: employees }
		});
	}, []);

	const importEmployees = useCallback(
		async (importedEmployees: Employee[]) => {
			setAppData((prevData) => ({
				...prevData,
				premium_variables: {
					...prevData?.premium_variables,
					employees: {
						...prevData?.premium_variables?.employees,
						employee_list: [
							...(prevData?.premium_variables?.employees?.employee_list || []),
							...importedEmployees
						]
					}
				}
			}));
		},
		[setAppData]
	);

	const replaceEmployees = useCallback(
		async (importedEmployees: Employee[]) => {
			setAppData((prevData) => ({
				...prevData,
				premium_variables: {
					...prevData?.premium_variables,
					employees: {
						...prevData?.premium_variables?.employees,
						employee_list: importedEmployees
					}
				}
			}));
		},
		[setAppData]
	);

	const updateAbsence = useCallback(
		(values: AbsenceValues) => {
			const rates_by_year = convertAbsenceValuesToRatesByYear(values);

			setAppData((prevData) => ({
				...prevData,
				premium_variables: {
					...prevData?.premium_variables,
					absence_info: {
						...prevData?.premium_variables?.absence_info,
						...values,
						rates_by_year
					}
				}
			}));
		},
		[setAppData]
	);

	const saveAbsence = useCallback(async (id: string, values: AbsenceValues) => {
		const rates_by_year = convertAbsenceValuesToRatesByYear(values);

		await apiService.premiumVars.update(id, {
			absence: {
				source: 'client',
				rates_by_year
			}
		});
	}, []);

	const updatePendingAbsenceValues = useCallback(
		(absenceValues: AbsenceValues) => {
			if (!setPendingPremiumVars) return;
			setPendingPremiumVars((prev) => ({
				...prev,
				absenceValues: {
					vz1: absenceValues.vz1 ?? '',
					vz2: absenceValues.vz2 ?? '',
					vz3: absenceValues.vz3 ?? ''
				}
			}));
		},
		[setPendingPremiumVars]
	);

	const updatePendingEmployeeTotals = useCallback(
		(employeesTotalWage: number, employeeCount: number) => {
			if (!setPendingPremiumVars) return;
			setPendingPremiumVars((prev) => ({
				...prev,
				employeesTotalWage,
				employeeCount
			}));
		},
		[setPendingPremiumVars]
	);

	const updateCoverage = useCallback(
		(values: CoverageValues) => {
			setAppData((prevData) => ({
				...prevData,
				current_policy: {
					...prevData?.current_policy,
					sick_leave_short_term: {
						...prevData?.current_policy?.sick_leave_short_term,
						coverage: {
							...prevData?.current_policy?.sick_leave_short_term?.coverage,
							waiting_period: String(values.waiting_period),
							employer_costs: values.employer_costs,
							coverage_percentage_first_year_first_half:
								values.coverage_percentage_first_year_first_half,
							coverage_percentage_first_year_second_half:
								values.coverage_percentage_first_year_second_half,
							coverage_percentage_second_year_first_half:
								values.coverage_percentage_second_year_first_half,
							coverage_percentage_second_year_second_half:
								values.coverage_percentage_second_year_second_half
						}
					}
				}
			}));
		},
		[setAppData]
	);

	const saveCoverage = useCallback(async (id: string, values: CoverageValues) => {
		await apiService.premiumVars.update(id, {
			coverage: {
				source: 'client',
				sick_leave_short_term: {
					...values,
					source: 'client'
				}
			}
		});
	}, []);

	const updatePendingCoverageValues = useCallback(
		(coverageValues: CoverageValues | null) => {
			if (!setPendingPremiumVars) return;
			setPendingPremiumVars((prev) => ({
				...prev,
				coverageValues
			}));
		},
		[setPendingPremiumVars]
	);

	const save = useCallback(
		async (
			id: string,
			{
				employees = null,
				absenceValues = null,
				coverageValues = null
			}: {
				employees?: Employee[] | null;
				absenceValues?: AbsenceValues | null;
				coverageValues?: CoverageValues | null;
			}
		) => {
			const payload: PremiumVarsPayload = {};

			// Add employees data if provided
			if (employees && employees.length > 0) {
				payload.employees = { source: 'client' as const, employee_list: employees };
			}

			// Add absence data if provided
			if (absenceValues) {
				const rates_by_year = convertAbsenceValuesToRatesByYear(absenceValues);

				payload.absence = {
					source: 'client' as const,
					rates_by_year
				};
			}

			// Add coverage data if provided. Mark it as client-sourced (mirrors
			// `saveCoverage`) so confirming coverage clears its warning triangle.
			if (coverageValues) {
				payload.coverage = {
					source: 'client' as const,
					sick_leave_short_term: {
						...coverageValues,
						source: 'client'
					}
				};
			}

			// Make single API call with combined payload
			await apiService.premiumVars.update(id, payload);
		},
		[]
	);

	return {
		employees: {
			add: addEmployee,
			update: updateEmployee,
			bulkUpdate: bulkUpdateEmployees,
			remove: removeEmployee,
			save: saveEmployees,
			import: importEmployees,
			replace: replaceEmployees,
			updatePendingTotals: updatePendingEmployeeTotals
		},
		absence: {
			update: updateAbsence,
			save: saveAbsence,
			updatePendingValues: updatePendingAbsenceValues
		},
		coverage: {
			update: updateCoverage,
			save: saveCoverage,
			updatePendingValues: updatePendingCoverageValues
		},
		save
	};
}
